// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  SI_CHECK_THEME_STATUS_REQUEST,
  SI_CHECK_THEME_STATUS_SUCCESS,
  SI_CHECK_THEME_STATUS_FAILURE,
  SI_THEME_INTEGRATION_SETUP_REQUEST,
  SI_THEME_INTEGRATION_SETUP_SUCCESS,
  SI_THEME_INTEGRATION_SETUP_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_REQUEST,
  SI_THEME_INTEGRATION_UPGRADE_SUCCESS,
  SI_THEME_INTEGRATION_UPGRADE_FAILURE,
  SI_THEME_INTEGRATION_SETTINGS_REQUEST,
  SI_THEME_INTEGRATION_SETTINGS_SUCCESS,
  SI_THEME_INTEGRATION_SETTINGS_FAILURE,
  SI_THEME_INTEGRATION_UPDATE_FIELD,
  SI_THEME_INTEGRATION_UPDATE_MULTI_FIELDS,
  SI_THEME_INTEGRATION_FEATURE_REQUEST,
  SI_THEME_INTEGRATION_FEATURE_SUCCESS,
  SI_THEME_INTEGRATION_FEATURE_FAILURE,
  SI_THEME_INTEGRATION_SIMPLIFY_REQUEST,
  SI_THEME_INTEGRATION_SIMPLIFY_SUCCESS,
  SI_THEME_INTEGRATION_SIMPLIFY_FAILURE,
  SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_REQUEST,
  SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_SUCCESS,
  SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_FAILURE,
  ParamsUpdateFeatures,
  ParamsSetupSimplify,
  SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_REQUEST,
  SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_SUCCESS,
  SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_REQUEST,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_SUCCESS,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_CLEAN_UP,
  SI_CHECK_LIVE_THEME_TURBO_REQUEST,
  SI_CHECK_LIVE_THEME_TURBO_SUCCESS,
  SI_CHECK_LIVE_THEME_TURBO_FAILURE,
  SEND_REQUEST_NEW_INTEGRATION_TO_SLACK,
  SEND_REQUEST_NEW_INTEGRATION_TO_SLACK_SUCCESS,
  SEND_REQUEST_NEW_INTEGRATION_TO_SLACK_FAILURE
} from 'stores/flow-type/shopify-integration/theme-integration.type';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';

/**
 * call to get list of all themes api (v3 api, v3 of update phase, not theme v3)
 * @return {object} The RSAA object for API middleware
 */
export const checkThemeIntegrationStatus = (
  i18n: I18n,
  themeId: Number,
  isOnboarding: boolean = false
) => {
  const params = { themeId };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.check-integration-status',
    { params, queries: { isOnboarding } },
    [
      SI_CHECK_THEME_STATUS_REQUEST,
      {
        type: SI_CHECK_THEME_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_CHECK_THEME_STATUS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to setup theme integration to specific store
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationSetup = (
  i18n: I18n,
  themeId: String,
  templateId: String,
  themeNameLib: String,
  isDefault: boolean,
  isLiveThemeChange: boolean = false
) => {
  const params = { themeId };
  const body = { templateId, isDefault, themeNameLib, isLiveThemeChange };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-setup',
    { params, body },
    [
      SI_THEME_INTEGRATION_SETUP_REQUEST,
      {
        type: SI_THEME_INTEGRATION_SETUP_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_SETUP_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ShopifyIntegration.ActionAPI.changeTemplate_Failed'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to upgrade theme integration to specific store
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationUpgrade = (
  i18n: I18n,
  themeId: String,
  templateId: String,
  version: String
) => {
  const params = { themeId };
  const body = { templateId, version };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-upgrade',
    { params, body },
    [
      SI_THEME_INTEGRATION_UPGRADE_REQUEST,
      {
        type: SI_THEME_INTEGRATION_UPGRADE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_UPGRADE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

export const getThemeSettings = (i18n: I18n, templateId: string) => {
  const params = { templateId };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.get-theme-settings',
    { params },
    [
      SI_THEME_INTEGRATION_SETTINGS_REQUEST,
      {
        type: SI_THEME_INTEGRATION_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_SETTINGS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
            i18n
          )
      }
    ]
  );
};

/**
 * Call to update field in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateField = (fieldName: string, data: any) => {
  return { type: SI_THEME_INTEGRATION_UPDATE_FIELD, payload: { fieldName, data } };
};

/**
 * Call to update multi fields in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateMultiFields = (data: any) => {
  return { type: SI_THEME_INTEGRATION_UPDATE_MULTI_FIELDS, payload: { data } };
};

/**
 * call to add feature theme integration to specific store
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationIntegrateFeature = (themeId: String, features: String[]) => {
  const params = { themeId };
  const body = { features };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-feature',
    { params, body },
    [
      SI_THEME_INTEGRATION_FEATURE_REQUEST,
      {
        type: SI_THEME_INTEGRATION_FEATURE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_FEATURE_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data, code: res.status };
        }
      }
    ]
  );
};

/**
 * call to add setup simplify theme integration
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationIntegrateSimplify = (
  themeId: string,
  body: ParamsSetupSimplify,
  i18n: I18n
) => {
  const params = { themeId };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-simplify',
    { params, body },
    [
      SI_THEME_INTEGRATION_SIMPLIFY_REQUEST,
      {
        type: SI_THEME_INTEGRATION_SIMPLIFY_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_SIMPLIFY_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          showSABToast(
            i18n.translate('AdminHTML.ShopifyIntegration.Simplify.Action.installTheme_Failed'),
            true
          );
          return data;
        }
      }
    ]
  );
};

/**
 * call to change features of simplify theme integration
 * @return {object} The RSAA object for API middleware
 */
export const themeIntegrationIntegrateChangeFeatures = (
  themeId: string,
  body: ParamsUpdateFeatures,
  i18n: I18n
) => {
  const params = { themeId };
  return apiService.sendRequest(
    'shopify-integration.theme-integration.theme-integration-change-features',
    { params, body },
    [
      SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_REQUEST,
      {
        type: SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_FAILURE,
        payload: async (action, state, res) => {
          const data = await await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ShopifyIntegration.Simplify.Action.updateFeatures_Failed'),
            i18n
          );
          return data;
        }
      }
    ]
  );
};

export const createTemplateInstanceFromThemeSettings = (
  themeId: String,
  onSuccess?: (data: unknown) => void,
  onFailure?: (data: unknown) => void
) => {
  const params = { themeId };

  return apiService.sendRequest(
    'shopify-integration.theme-integration.create-template-instance-from-theme-settings',
    { params },
    [
      {
        type: SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_REQUEST,
        payload: async () => ({
          themeId
        })
      },
      {
        type: SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          if (onSuccess) {
            onSuccess(data);
          }

          return { data, themeId };
        }
      },
      {
        type: SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          if (onFailure) {
            onFailure(data);
          }

          return { themeId, data, code: res.status };
        }
      }
    ]
  );
};

export const upgradeToTurbo = (
  themeId: String,
  templateId: String,
  onSuccess?: (data: any) => void,
  onFailure?: (data: any) => void
) => {
  const params = {
    themeId
  };

  const body = { templateId };

  return apiService.sendRequest(
    'shopify-integration.theme-integration.upgrade-to-turbo',
    { params, body },
    [
      {
        type: SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_REQUEST,
        payload: async () => {
          return {
            themeId
          };
        }
      },
      {
        type: SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          if (onSuccess) onSuccess(data);

          return { data, themeId };
        }
      },
      {
        type: SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          if (onFailure) onFailure(data);

          return { themeId, data, code: res.status };
        }
      }
    ]
  );
};

export const upgradeToTurboCleanUp = (themeId?: string) => {
  return { type: SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_CLEAN_UP, payload: { themeId } };
};

export const checkLiveThemeTurbo = (i18n: I18n) => {
  return apiService.sendRequest(
    'shopify-integration.theme-integration.check-live-theme-turbo',
    {},
    [
      SI_CHECK_LIVE_THEME_TURBO_REQUEST,
      {
        type: SI_CHECK_LIVE_THEME_TURBO_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: SI_CHECK_LIVE_THEME_TURBO_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ShopifyIntegration.fetchAPI.checkLiveThemeTurbo_Fail'),
            i18n
          )
      }
    ]
  );
};

export const sendRequestNewIntegrationToSlack = (name, link, info) => {
  return apiService.sendRequest(
    'admin-v3.theme-setup.request-new-integration',
    { body: { name, link, info } },
    [
      SEND_REQUEST_NEW_INTEGRATION_TO_SLACK,
      {
        type: SEND_REQUEST_NEW_INTEGRATION_TO_SLACK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          if (res.status === 200) {
            showSABToast('Your request has been received!', false);
          }
          return { data };
        }
      },
      {
        type: SEND_REQUEST_NEW_INTEGRATION_TO_SLACK_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          showSABToast('Something went wrong, please try again.', true);
          return { data };
        }
      }
    ]
  );
};
